// src/pages/AuthPage.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext'; // Make sure resetPassword is provided by context
import { useNavigate, Link } from 'react-router-dom'; // Import Link for legal pages
import { EyeIcon, EyeSlashIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';

// --- Password Validation Function (Keep as is or import from utils) ---
const validatePassword = (password) => {
    const errors = [];
    const minLength = 8;
    if (password.length < minLength) errors.push(`at least ${minLength} characters`);
    if (!/[a-z]/.test(password)) errors.push("a lowercase letter");
    if (!/[A-Z]/.test(password)) errors.push("an uppercase letter");
    if (!/\d/.test(password)) errors.push("a number");
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/.test(password)) errors.push("a special character (!@#$...)");
    return errors;
};
// ---

const AuthPage = () => {
    // --- State Variables ---
    const [mode, setMode] = useState('login'); // 'login', 'signup', or 'forgot'
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [passwordTouched, setPasswordTouched] = useState(false);
    const [passwordValidationErrors, setPasswordValidationErrors] = useState([]);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    // Removed resetEmail state, reuse 'email' state for forgot password form
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    const [showVerificationPendingModal, setShowVerificationPendingModal] = useState(false); // New state for the modal

    // --- Hooks ---
    // Ensure resetPassword is included from your context
    const { signInWithPassword, signUp, signInWithGoogle, resetPassword } = useAuth();
    const navigate = useNavigate();

    // --- Derived State ---
    const isLogin = mode === 'login';
    const isSignup = mode === 'signup';
    const isForgotPassword = mode === 'forgot';

    // --- Effect for Dynamic Password Validation Feedback ---
    useEffect(() => {
        // Only validate and show requirements during signup when touched
        if (isSignup && passwordTouched) {
            setPasswordValidationErrors(validatePassword(password));
        } else {
            setPasswordValidationErrors([]); // Clear otherwise
        }
    }, [password, isSignup, passwordTouched]);

    // --- Form Submission Handler (Login/Signup) ---
    const handleEmailPasswordSubmit = async (e) => {
        e.preventDefault();
        setMessage(''); setError('');

        if (isSignup) {
            // Signup Validations
            if (!agreedToTerms) { setError("You must agree to the Terms and Privacy Policy."); return; }
            const complexityErrors = validatePassword(password);
            if (complexityErrors.length > 0) { setError(`Password must contain: ${complexityErrors.join(', ')}.`); setPasswordTouched(true); return; }
            if (password !== confirmPassword) { setError("Passwords do not match."); return; }
            if (!firstName.trim() || !lastName.trim()) { setError("First and last name are required."); return; }
        }

        setLoading(true);
        try {
            let response;
            if (isLogin) {
                // --- LOGIN ---
                response = await signInWithPassword({ email, password });
                if (response.error) throw response.error;
                navigate('/main-menu'); // Navigate after successful login
            } else {
                // --- SIGN UP ---
                response = await signUp({
                    email, password,
                    options: { data: { first_name: firstName.trim(), last_name: lastName.trim() } }
                });
                if (response.error) throw response.error;
                // Handle success (confirmation email or auto-login)
                 if (response.data.user && !response.data.session) {
                    // setMessage('Account created! Please check your email to verify your account before logging in.'); /* Clear fields */ clearForm();
                    setShowVerificationPendingModal(true); // Show new modal
                    // Form will be cleared when modal is dismissed by switchMode
                 } else if (response.data.user && response.data.session) {
                    setMessage('Sign up successful! Redirecting...');
                    navigate('/main-menu');
                 } else {
                    setError("Sign up completed, but state is unclear.");
                 }
            }
        } catch (err) {
            console.error("Auth error:", err);
            // Map common errors (keep your existing mapping)
            if (err.message.includes("Invalid login credentials")) { setError("Invalid email or password."); }
            else if (err.message.includes("User already registered")) { setError("An account with this email already exists. Please log in instead."); }
            else if (err.message.includes("Password should be at least")) { setError(err.message); } // Show Supabase's length error if it somehow gets triggered
            else if (err.message.includes("profiles_check") || err.message.includes("constraint")) { setError("First/Last name cannot be empty or invalid.");} // From DB check constraint
            else if (err.message.includes("confirmation token is invalid")) { setError("Invalid or expired confirmation token. Please sign up again or request a password reset."); }
            else { setError(err.message || 'An unexpected error occurred.'); }
        } finally {
            setLoading(false);
        }
    };

    // --- Google Sign In Handler (Keep as is) ---
    const handleGoogleSignIn = async () => {
        setMessage(''); setError(''); setLoading(true);
        try {
            const { error } = await signInWithGoogle();
            if (error) throw error;
            // Redirect is handled by Supabase OAuth flow
        } catch (err) {
            console.error("Google Sign In error:", err);
            setError(err.message || 'Failed to start Google Sign In.');
            setLoading(false); // Only stop loading on error, success redirects
        }
    };

    // --- Forgot Password Handler ---
     const handlePasswordReset = async (e) => {
        e.preventDefault();
        setMessage(''); setError('');
        if (!email.trim()) { // Reuse email state
            setError("Please enter the email address for your account.");
            return;
        }
        setLoading(true);
        try {
            const { error: resetError } = await resetPassword(email); // Use email state
            if (resetError) throw resetError;
            setMessage('Password reset instructions sent! Please check your email.');
            // Optionally switch back to login after a delay or keep user here
            // setTimeout(() => setMode('login'), 4000);
        } catch (err) {
            console.error("Password Reset error:", err);
            if (err.message.includes("User not found")) { setError("No account found with that email address."); }
            else if (err.message.includes("rate limit")) { setError("Too many requests. Please wait and try again."); }
            else { setError(err.message || 'Failed to send password reset email.'); }
        } finally {
            setLoading(false);
        }
    };


    // --- Helper to clear form fields and messages ---
    const clearForm = () => {
        setError(''); setMessage('');
        setEmail(''); setPassword(''); setConfirmPassword('');
        setFirstName(''); setLastName('');
        setPasswordTouched(false); setPasswordValidationErrors([]);
        setShowPassword(false); setShowConfirmPassword(false);
        setAgreedToTerms(false);
        // Do not clear showVerificationPendingModal here, it's handled by its own logic
    };

    // --- Switch Mode Handler ---
     const switchMode = (newMode) => {
        setMode(newMode);
        clearForm(); // Clear everything when switching modes
    };


    // --- Password Requirements Display Component ---
    const PasswordRequirements = () => {
        if (!isSignup || !passwordTouched) return null; // Show only on signup when touched
        const requirements = [
             { label: "At least 8 characters", met: password.length >= 8 },
             { label: "A lowercase letter (a-z)", met: /[a-z]/.test(password) },
             { label: "An uppercase letter (A-Z)", met: /[A-Z]/.test(password) },
             { label: "A number (0-9)", met: /\d/.test(password) },
             { label: "A special character (!@#$...)", met: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/.test(password) },
         ];
        return (
            <div className="text-xs mt-2 pl-1 space-y-1">
                <p className="font-medium mb-1 text-base-content/70">Password must contain:</p>
                <ul className="list-inside">
                    {requirements.map(req => (
                        <li key={req.label} className={`transition-colors duration-200 ${req.met ? 'text-success line-through opacity-70' : 'text-error'}`}> {req.label} </li>
                     ))}
                 </ul>
            </div>
        );
    };

    // --- Render Component ---
    return (
        <div className="min-h-screen flex items-center justify-center bg-base-200 py-12 px-4 sm:px-6 lg:px-8">
            <div className="card w-full max-w-md bg-base-100 shadow-xl">
                <div className="card-body">
                    {/* Back Button (only shown in Forgot Password view) */}
                    {isForgotPassword && (
                         <button onClick={() => switchMode('login')} className="btn btn-ghost btn-sm justify-start p-0 mb-4 hover:bg-transparent text-base-content/80 hover:text-primary" aria-label="Back to Login" disabled={loading}>
                            <ArrowLeftIcon className="w-4 h-4 mr-1" /> Back to Login
                         </button>
                     )}
                    {/* Logo */}
                    <div className="flex justify-center mb-4"> {/* Centering container for the logo. mb-4 provides space below the logo. */}
                        <img 
                            src="/twifwave.png" 
                            alt="TWIF Logo"    
                            className="h-16 w-auto" 
                        />
                    </div>
                    {/* Title */}
                    <h2 className="card-title justify-center text-2xl mb-6 font-bold text-base-content">
                        {isForgotPassword ? 'Reset Your Password' : (isLogin ? 'Login to Your Account' : 'Create Your Account')}
                    </h2>

                    {/* Error & Message Alerts */}
                    {error && ( <div role="alert" className="alert alert-error shadow-lg mb-4"> <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg> <span>{error}</span> </div> )}
                    {message && !showVerificationPendingModal && ( <div role="alert" className="alert alert-success shadow-lg mb-4"> <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg> <span>{message}</span> </div> )}


                    {/* --- FORGOT PASSWORD FORM --- */}
                    {isForgotPassword && (
                        <form onSubmit={handlePasswordReset} className="space-y-4">
                            <p className="text-sm text-base-content/80 text-center">
                                Enter your account's email address below to receive password reset instructions.
                            </p>
                            <div className="form-control">
                                <label className="label"><span className="label-text text-base-content/80">Email address</span></label>
                                <input type="email" placeholder="you@example.com" autoComplete='email' className="input input-bordered w-full" value={email} onChange={(e) => setEmail(e.target.value)} required disabled={loading} />
                            </div>
                            <div className="form-control pt-2">
                                <button type="submit" className="btn btn-primary w-full" disabled={loading}> {loading ? <span className="loading loading-spinner loading-sm"></span> : 'Send Reset Link'} </button>
                            </div>
                        </form>
                    )}

                    {/* --- LOGIN / SIGN UP FORM --- */}
                    {!isForgotPassword && (
                        <form onSubmit={handleEmailPasswordSubmit} className="space-y-4">
                            {/* Signup Fields */}
                            {isSignup && (
                                <>
                                    <div className="form-control"> <label className="label"><span className="label-text text-base-content/80">First Name</span></label> <input type="text" placeholder="Your first name" className="input input-bordered w-full" value={firstName} onChange={(e) => setFirstName(e.target.value)} required={isSignup} disabled={loading} /> </div>
                                    <div className="form-control"> <label className="label"><span className="label-text text-base-content/80">Last Name</span></label> <input type="text" placeholder="Your last name" className="input input-bordered w-full" value={lastName} onChange={(e) => setLastName(e.target.value)} required={isSignup} disabled={loading} /> </div>
                                </>
                             )}

                            {/* Email */}
                            <div className="form-control"> <label className="label"><span className="label-text text-base-content/80">Email address</span></label> <input type="email" placeholder="you@example.com" autoComplete='email' className="input input-bordered w-full" value={email} onChange={(e) => setEmail(e.target.value)} required disabled={loading} /> </div>

                            {/* Password */}
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text text-base-content/80">Password</span>
                                    {/* Show Forgot Password link only on Login screen */}
                                    {isLogin &&
                                        <button type="button" onClick={() => switchMode('forgot')} className="link link-hover link-primary text-xs p-0 font-normal" disabled={loading}> {/* Changed to primary color */}
                                            Forgot password?
                                        </button>
                                    }
                                </label>
                                <div className="relative"> <input type={showPassword ? 'text' : 'password'} placeholder="••••••••" autoComplete={isLogin ? 'current-password' : 'new-password'} className="input input-bordered w-full pr-10" value={password} onBlur={() => setPasswordTouched(true)} onChange={(e) => setPassword(e.target.value)} required disabled={loading} /> <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 px-3 flex items-center text-base-content/50 hover:text-base-content/70 focus:outline-none" aria-label={showPassword ? "Hide password" : "Show password"}> {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />} </button> </div>
                            </div>

                            {/* Password Requirements (Signup) */}
                            {isSignup && <PasswordRequirements />}

                            {/* Confirm Password (Signup) */}
                            {isSignup && ( <div className="form-control"> <label className="label"><span className="label-text text-base-content/80">Confirm Password</span></label> <div className="relative"> <input type={showConfirmPassword ? 'text' : 'password'} placeholder="••••••••" autoComplete="new-password" className="input input-bordered w-full pr-10" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required={isSignup} disabled={loading} /> <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute inset-y-0 right-0 px-3 flex items-center text-base-content/50 hover:text-base-content/70 focus:outline-none" aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}> {showConfirmPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />} </button> </div> </div> )}

                            {/* Terms and Privacy Checkbox (Signup) */}
                             {isSignup && (
                                <div className="form-control">
                                  <label className="label cursor-pointer justify-start gap-3 py-1">
                                    <input type="checkbox" className="checkbox checkbox-primary checkbox-xs" checked={agreedToTerms} onChange={(e) => setAgreedToTerms(e.target.checked)} disabled={loading} />
                                    <span className="label-text text-xs text-base-content/80"> I agree to the{' '} <Link to="/terms" target="_blank" rel="noopener noreferrer" className="link link-primary"> Terms of Service </Link> {' '}and{' '} <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="link link-primary"> Privacy Policy </Link> </span>
                                  </label>
                                </div>
                             )}

                            {/* Submit Button */}
                            <div className="form-control pt-2">
                                <button type="submit" className="btn btn-primary w-full" disabled={loading || (isSignup && !agreedToTerms)}> {loading ? <span className="loading loading-spinner loading-sm"></span> : (isLogin ? 'Login' : 'Sign Up')} </button>
                            </div>
                        </form>
                    )}

                    {/* --- Toggle Mode / OR Divider / Google Button --- */}
                    {/* Only show if not in Forgot Password mode */}
                    {!isForgotPassword && (
                        <>
                             {/* Toggle Login/Signup Link */}
                             <div className={`mt-6 ${isLogin ? 'form-control' : 'text-center text-sm'}`}>
                                 <button
                                     onClick={() => switchMode(isLogin ? 'signup' : 'login')}
                                     className={isLogin ? "btn btn-outline btn-primary w-full" : "link link-hover text-base-content/80 hover:text-primary"}
                                     disabled={loading}
                                 >
                                     {isLogin ? "Don't have an account? Sign Up" : 'Already have an account? Login'}
                                 </button>
                             </div>
                             {/* OR Divider */}
                             <div className="divider my-6 text-base-content/50 text-sm">OR</div>
                             {/* Google Sign In */}
                             <div className="form-control">
                                 <button onClick={handleGoogleSignIn} className="btn btn-outline border-base-content/20 hover:bg-base-content/10 w-full" disabled={loading}> <img src="/GoogleIcon.svg" alt="Google icon" className="w-5 h-5 mr-2"/> Continue with Google </button>
                                 {isSignup && <p className="text-xs text-center mt-2 opacity-70">You can add first/last name later if signing up with Google.</p>}
                             </div>
                        </>
                    )}

                    {/* --- Privacy and Terms Links Footer --- */}
                    <div className="mt-8 text-center text-xs text-base-content/60 border-t border-base-300 pt-4"> {/* Added border-top and padding */}
                         <Link to="/terms" target="_blank" rel="noopener noreferrer" className="link link-hover">Terms of Service</Link>
                         <span className="mx-2">|</span>
                         <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="link link-hover">Privacy Policy</Link>
                    </div>

                </div> {/* End card-body */}
            </div> {/* End card */}

            {/* Verification Pending Modal */}
            {showVerificationPendingModal && (
                <div className="modal modal-open">
                    <div className="modal-box">
                        <h3 className="font-bold text-lg text-center">Account Created!</h3>
                        <p className="py-4 text-center">
                            Thanks for signing up for The Week In Fantasy!
                            <br />
                            A verification email has been sent to <strong className="text-primary">{email}</strong>.
                            <br />
                            Please check your inbox (and spam folder) to activate your account before logging in.
                        </p>
                        <div className="modal-action justify-center">
                            <button 
                                onClick={() => {
                                    setShowVerificationPendingModal(false);
                                    switchMode('login'); // This will also clear the form
                                }} 
                                className="btn btn-primary"
                            >
                                OK, Got It!
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div> // End page container
    );
};

export default AuthPage;